import React from 'react';
import {
  AppBar, Toolbar, Menu, MenuItem, IconButton, Typography,
  Box, Checkbox, FormControlLabel, Tooltip, Select, SelectChangeEvent
} from '@mui/material';
import {
  Menu as FileIcon, Save as SaveIcon, SaveAlt as SaveAltIcon,
  FolderOpen as FolderOpenIcon, Add as AddIcon, Help as HelpIcon,
  Favorite as FavoriteIcon, Language as LanguageIcon,
  DeleteOutline as EraserIcon, FormatColorFill as RecolorIcon
} from '@mui/icons-material';
import { AlignmentSettings, ActiveTool } from '../../types/pattern';
import { useLocalization, Language } from '../../localization/LocalizationContext';
import { HelpDialog } from '../HelpDialog';

interface MenuBarProps {
  settings: AlignmentSettings;
  onSettingsChange: (settings: AlignmentSettings) => void;
  onNewFile: () => void;
  onSaveImage: (format: 'png' | 'jpg') => void;
  onExportPattern: () => void;
  onImportPattern: () => void;
  symmetryAxes: number;
  onSymmetryChange: (axes: number) => void;
  activeTool: ActiveTool;
  onToolChange: (tool: ActiveTool) => void;
}

const languageNames: Record<string, string> = {
  'en': 'English (US)', 'en-GB': 'English (UK)', 'ru': 'Русский',
  'de': 'Deutsch', 'cs': 'Čeština', 'es': 'Español',
  'ja': '日本語', 'fr': 'Français', 'pt': 'Português'
};

const languageFlags: Record<string, string> = {
  'en': '🇺🇸', 'en-GB': '🇬🇧', 'ru': '🇷🇺', 'de': '🇩🇪',
  'es': '🇪🇸', 'ja': '🇯🇵', 'fr': '🇫🇷', 'pt': '🇧🇷', cs: '🇨🇿'
};

export const MenuBar: React.FC<MenuBarProps> = ({
  settings, onSettingsChange, onNewFile, onSaveImage,
  onExportPattern, onImportPattern, symmetryAxes, onSymmetryChange,
  activeTool, onToolChange
}) => {
  const [fileAnchorEl, setFileAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isHelpOpen, setIsHelpOpen] = React.useState(false);
  const { t, language, setLanguage } = useLocalization();

  const handleMenuClose = () => setFileAnchorEl(null);

  const handleToggle = (setting: keyof AlignmentSettings) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onSettingsChange({ ...settings, [setting]: event.target.checked });
  };

  return (
    <>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar variant="dense" sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton edge="start" color="inherit" aria-label="file menu" onClick={e => setFileAnchorEl(e.currentTarget)} size="small">
              <FileIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={fileAnchorEl} open={Boolean(fileAnchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={() => { onNewFile(); handleMenuClose(); }} sx={{ userSelect: 'none' }}>
                <AddIcon sx={{ mr: 1 }} /> {t.menuBar.file.newFile}
              </MenuItem>
              <MenuItem onClick={() => { onImportPattern(); handleMenuClose(); }} sx={{ userSelect: 'none' }}>
                <FolderOpenIcon sx={{ mr: 1 }} /> {t.menuBar.file.openProject}
              </MenuItem>
              <MenuItem onClick={() => { onExportPattern(); handleMenuClose(); }} sx={{ userSelect: 'none' }}>
                <SaveAltIcon sx={{ mr: 1 }} /> {t.menuBar.file.saveProject}
              </MenuItem>
              <MenuItem onClick={() => { onSaveImage('png'); handleMenuClose(); }} sx={{ userSelect: 'none' }}>
                <SaveIcon sx={{ mr: 1 }} /> {t.menuBar.file.savePNG}
              </MenuItem>
              <MenuItem onClick={() => { onSaveImage('jpg'); handleMenuClose(); }} sx={{ userSelect: 'none' }}>
                <SaveIcon sx={{ mr: 1 }} /> {t.menuBar.file.saveJPG}
              </MenuItem>
            </Menu>

            <FormControlLabel
              control={<Checkbox checked={settings.showGuides} onChange={handleToggle('showGuides')} size="small" />}
              label={<Typography variant="body2" fontSize={13}>{t.menuBar.display.showGuides}</Typography>}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', borderLeft: '1px solid #e0e0e0', pl: 1 }}>
              <Typography variant="body2" fontSize={13} sx={{ mr: 0.5 }}>{t.stitchesPanel.symmetry}:</Typography>
              <Select value={symmetryAxes} onChange={(e: SelectChangeEvent<number>) => onSymmetryChange(Number(e.target.value))} size="small" sx={{ minWidth: 55, height: 28, fontSize: 13 }}>
                <MenuItem value={0}>{t.stitchesPanel.symmetryOff}</MenuItem>
                {[2,3,4,5,6,8,9,10,12,15,18,20,24,30,36].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderLeft: '1px solid #e0e0e0', pl: 1 }}>
              <Tooltip title={t.stitchesPanel.eraser} enterDelay={500} disableInteractive>
                <IconButton onClick={() => onToolChange('eraser')} size="small"
                  sx={{ border: activeTool === 'eraser' ? '2px solid #d32f2f' : '1px solid #e0e0e0', borderRadius: 1, p: 0.5, color: activeTool === 'eraser' ? '#d32f2f' : undefined }}>
                  <EraserIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t.stitchesPanel.recolor} enterDelay={500} disableInteractive>
                <IconButton onClick={() => onToolChange('recolor')} size="small"
                  sx={{ border: activeTool === 'recolor' ? '2px solid #1976d2' : '1px solid #e0e0e0', borderRadius: 1, p: 0.5, color: activeTool === 'recolor' ? '#1976d2' : undefined }}>
                  <RecolorIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton color="inherit" onClick={() => setIsHelpOpen(true)} size="small">
              <HelpIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Tooltip title={t.menuBar.donate} enterDelay={500} disableInteractive>
              <IconButton color="primary" onClick={() => window.open('https://ko-fi.com/hvapp', '_blank')} size="small" sx={{ '&:hover': { color: '#FF5F5F' } }}>
                <FavoriteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Select value={language} onChange={(event: SelectChangeEvent<string>) => setLanguage(event.target.value as Language)} size="small"
              sx={{ minWidth: 120, height: 28, fontSize: 13, '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 0.5 } }} IconComponent={LanguageIcon}>
              {Object.entries(languageNames).map(([code, name]) => (
                <MenuItem key={code} value={code} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{languageFlags[code]}</span>
                  <span>{name}</span>
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Toolbar>
      </AppBar>

      <HelpDialog open={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
    </>
  );
};
